/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import filesPageUrl1 from "url:../assets/images/files_1.png";
import filesPageUrl2 from "url:../assets/images/files_2.png";

export const filesPageContent = `

## Summary

The Files page provides a centralized view of all files that have been uploaded to Dexter and are available for use in input collections and transformation pipelines. This page serves as a reference library where you can:

- **Browse all uploaded files** in a searchable, sortable table
- **View file metadata** including upload date, size, and the input collection they belong to
- **Inspect file contents** in a syntax-highlighted code preview
- **Discover relationships** between files and input collections
- **Download files** for use outside of Dexter

Unlike the Input Collections page where you organize files into logical groups, the Files page shows the underlying files themselves - the raw data assets that power your transformations.

---

## Files Tab Overview

The Files tab displays all your uploaded files in a clean, searchable interface.

<img src="${filesPageUrl1}" alt="Files tab showing file list" style="max-width: 900px; height: auto; display: block; margin: 24px auto;" />

### Page Anatomy

**Top Section:**
- **Search bar** - Filter files by filename
- Centered, prominent search field
- Results update automatically as you type (real-time search)

**File Table:**
The table displays your files with the following columns:
- **File Name** - Full filename with extension (clickable to view details)
- **File Type** - Type of file (e.g., PROTO, XML, etc.)
- **Upload Date** - When the file was uploaded to Dexter
- **File Size** - Size in MB, KB, or bytes

**Table Features:**
- **Sortable columns** - Click headers to sort ascending/descending
- **Clickable rows** - Click filename to open file details
- **Hover effects** - Rows highlight on hover
- **Pagination** - Reduce information load by displaying only a certain number of scrollable files at a time
- **Deletion** - Select files and click the trash can icon to remove files you no longer need

**Active Files:**
Files currently referenced by one or more input collections appear in this list. These are the files Dexter knows about and can use in transformations.

**Hidden Files:**
- **PDF files are automatically hidden** from this list
- PDFs are processed to extract structured data
- The extracted data files appear instead
- This keeps the list focused on usable data files

---

## Understanding Files vs. Collections

It's important to understand the relationship between Files and Input Collections:

### Files Are the Raw Assets

**What They Are:**
- Individual data files (XML, JSON, Protobuf, etc.)
- Uploaded through Input Collections or Transform Engine
- Stored in Dexter's file system
- Contain the actual data used in transformations

**Where They Live:**
- Physical storage on Dexter's servers
- Tracked in Dexter's database
- Referenced by Input Collections
- Available for use in Transform Engine

### Input Collections Are the Organization

**What They Are:**
- Logical groupings of related files
- Contain metadata and folder structures
- Reference files without duplicating them
- Provide organization and reusability

**The Relationship:**
- One file can belong to multiple collections
- Deleting a collection doesn't delete the files, unless that file is no longer being used in ANY input collection
- Files persist independently of collections
- Collections are organizational pointers to files

---

## Searching for Files

The search bar helps you quickly find specific files among potentially hundreds or thousands of uploads.

### How Search Works

**What Gets Searched:**
- File names (not contents)
- Full filename including extension
- File type
- Case-insensitive matching
- Partial matches included

### Using Search

**Basic Search:**
1. Click in the search bar at the top of the page
2. Type any part of the filename or file type you're looking for
3. Search results automatically update (real time search)

**Search Tips:**

**File Extensions:**
- Search "xml" to see all XML files
- Search "proto" to see all Protobuf files
- Search "json" to see all JSON files

**Partial Names:**
- "customer" finds "customer_data.xml" and "customers_2024.json"
- "v2" finds "data_v2.xml" and "schema_v2.proto"
- "2024" finds all files with "2024" in the name

---

## Sorting the File List

Click any column header to sort files by that attribute.

### Available Sort Columns

**File Name:**
- Alphabetical sorting (A-Z or Z-A)
- Case-insensitive
- Extensions sort with names
- Numbers sort naturally (file1, file2, file10)

**File Type:**
- Alphabetical sorting (A-Z or Z-A)
- Case-insensitive

**Upload Date:**
- Chronological sorting (oldest first or newest first)
- Default: Most recent first
- Useful for finding latest uploads

**File Size:**
- Numeric sorting (smallest to largest or vice versa)
- Sorts by actual byte size
- Display converts to KB/MB for readability
- Useful for finding large files

---

## Viewing File Details

Click any filename in the table to open the file details page with comprehensive information.

<img src="${filesPageUrl2}" alt="File details page" style="max-width: 900px; height: auto; display: block; margin: 24px auto;" />

### Page Layout Details
- **File name as title (1)** - Large, prominent display
- **File Metadata (2)** - Upload date, saved mappings containing the file, input collections containing this file, file size
  - Note that these associations can be removed from this page by clicking the X. Doing so removes it from the associated mapping or input collection.
- **File Contents (3)** - Syntax highlighted, raw content of file (if supported by Dexter)
- **Back Button (4)** - Return to file list
- **Download Button (5)** - Click to download the file to your computer

### Saved Mapping/Input Collection References

**What It Shows:**
- List of saved mapping and input collection titles that reference this file
- Displayed as colored pills/badges
- One pill per collection

**Why It Matters:**
- Understand where the file is being used
- See all contexts for this file
- Verify file is in expected mapping and collection
- Discover unexpected relationships

### File Contents Preview

The most powerful feature of the details page - see exactly what's in the file.

**Preview Display:**
- **Syntax highlighting** - Color-coded based on file type
- **Monospace font** - Clean, readable code display
- **Scrollable view** - Navigate through entire file

`;
